import {
    Children,
    isValidElement,
    cloneElement,
    FC,
    useContext,
    useState
} from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import triggerTracking from '@blueprint/tracking';
import { useModal } from '@blueprint/ui';
import {
    XhrResponseModalContent,
    ResponseType
} from './XhrResponseModalContent';
import { filtersValuesSelector } from '@zoopla/redux-reducer-filters';
import { polyencSelector } from '@zoopla/redux-reducer-search-results';
import { SearchContext } from '@zoopla/listing-search-context';
import { constructEditSavedSearchFields } from '@zoopla/listing-search-utils';
import { alertLinkClickHandler } from '@zoopla/listing-search/nav-with-auth';
import AlertModal from './AlertModal';
import {
    CREATE_SAVED_SEARCH,
    CREATE_EMAIL_ALERT
} from '@zoopla/listing-search-queries';
import { useIsLoggedIn } from '@zoopla/redux-reducer-user';
import { AjaxLinkClickProps, SimpleLinkClickProps } from './AlertPanel.types';
import { StyledModal } from './AlertPanel.styles';

const fireTrackingEvent = (category: string, label: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: category,
        eventAction: 'clickthrough',
        eventLabel: label
    });
};

const SaveSearchAlertModalWrapper: FC<React.PropsWithChildren<unknown>> = ({
    children
}) => {
    const [responseModalType, setResponseModalType] =
        useState<ResponseType>(null);

    const { state } = useContext(SearchContext);
    const router = useRouter();
    const isLoggedIn = useIsLoggedIn();

    const [isAlertModalShowing, , closeAlertModal] = useModal();
    const polyenc = useSelector(polyencSelector);
    const { push, asPath } = router;

    const { userAlertId, searchType } = state;

    const filters = useSelector(filtersValuesSelector);

    const onSaveSearchComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveSearchSuccess');
        } else {
            setResponseModalType('saveSearchFailure');
        }
    };

    const onSaveAlertComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveAlertSuccess');
        } else {
            setResponseModalType('saveAlertFailure');
        }
    };

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            onSaveSearchComplete(data.createNewSavedSearch.status);
        },
        onError: () => {
            setResponseModalType('saveSearchFailure');
        }
    });

    const [createEmailAlertMutation] = useMutation(CREATE_EMAIL_ALERT, {
        onCompleted: (data) => {
            onSaveAlertComplete(data.createNewEmailAlert.status);
        },
        onError: () => {
            setResponseModalType('saveAlertFailure');
        }
    });

    const onSimpleLinkClick: SimpleLinkClickProps = (
        evt,
        category,
        label,
        requiresAuth = false
    ) => {
        evt.preventDefault();

        if (label && category) {
            fireTrackingEvent(category, label);
        }

        alertLinkClickHandler({
            evt,
            router,
            requiresAuth,
            isLoggedIn
        });
    };

    const createSavedSearchOrAlert = (label?: string) => {
        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        if (label !== 'create_alert') {
            createSavedSearchMutation({
                variables: {
                    input
                }
            });
        } else {
            createEmailAlertMutation({
                variables: {
                    input
                }
            });
        }
    };

    const onAjaxLinkClick: AjaxLinkClickProps = async (
        evt,
        category,
        label
    ) => {
        evt.preventDefault();

        const destinationUrl = evt.currentTarget.dataset['url'] || asPath;
        const redirectUrl = encodeURIComponent(destinationUrl);
        const registerUrl = `${
            process.env['REGISTER_PATH'] || '/register/'
        }?page_after_login=${redirectUrl}&return_url=${redirectUrl}`;

        if (label && category) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: category,
                eventAction: 'clickthrough',
                eventLabel: label
            });
        }

        if (!isLoggedIn) {
            if (label === 'save_search') {
                push(registerUrl);
                return;
            }

            if (label === 'create_alert') {
                push(registerUrl);
                return;
            }
        }
        createSavedSearchOrAlert(label);
    };

    const onModalCloseClick = () => {
        setResponseModalType(null);
    };

    return (
        <>
            <AlertModal
                isShowing={isAlertModalShowing}
                close={closeAlertModal}
            />
            {Children.map(children, (child) => {
                if (isValidElement(child)) {
                    return cloneElement(child, {
                        onSimpleLinkClick: onSimpleLinkClick,
                        onAjaxLinkClick: onAjaxLinkClick
                    });
                }
                return null;
            })}
            <StyledModal
                isShowing={responseModalType !== null}
                close={onModalCloseClick}
            >
                <XhrResponseModalContent
                    responseType={responseModalType}
                    close={onModalCloseClick}
                    action={
                        responseModalType === 'saveSearchFailure'
                            ? () => createSavedSearchOrAlert('save_search')
                            : responseModalType === 'saveAlertFailure'
                            ? () => createSavedSearchOrAlert('save_alert')
                            : undefined
                    }
                />
            </StyledModal>
        </>
    );
};

export default SaveSearchAlertModalWrapper;
